export const TPAYGATE_ENVIRONMENTS = ["Staging", "Production"] as const;
export type TPaygateEnvironment = (typeof TPAYGATE_ENVIRONMENTS)[number];

export interface TPaygateConfigDto {
  configId?: string;
  clientId: string;
  tPayGateTenantId: string;
  source: string;
  environment: TPaygateEnvironment;
  webhookUrl?: string;
  /** Whether the integration is switched on. The credentials stay stored either way, so the
   * switch can be flipped back without re-entering them. */
  isActive: boolean;
  isConfigured: boolean;
  /** Whether a client secret is stored. The secret itself is never returned, so this is the only
   * way to tell "saved but hidden" apart from "missing" — and missing means every signed
   * T-PayGate call fails with 403. Optional because an API older than this field omits it; treat
   * `undefined` as unknown rather than missing. */
  hasClientSecret?: boolean;
  /** Whether this config targets the T-PayGate sandbox (Staging). The webhook simulator runs
   * only then. Optional for the same reason as `hasClientSecret`: `undefined` means unknown. */
  isSandbox?: boolean;
  updatedAt?: string;
}

export interface SaveTPaygateConfigRequest {
  configId?: string;
  clientId: string;
  tPayGateTenantId: string;
  source: string;
  environment: TPaygateEnvironment;
  webhookUrl?: string;
  clientSecret?: string;
  isActive: boolean;
}

export interface SupportedBankDto {
  code: string;
  name: string;
  shortName: string;
  logoUrl?: string;
  bin?: string;
}

export interface ConnectedBankDto {
  configBankId: string;
  bankCode: string;
  bankName?: string;
  bankLogoUrl?: string;
  accountNo: string;
  accountName: string;
  vaNumber?: string;
  merchantId?: string;
  phone?: string;
  isConnected: boolean;
  createdAt: string;
}

export interface ConnectBankRequest {
  bankCode: string;
  merchantName: string;
  accountName: string;
  accountNo: string;
  identity?: string;
  phone?: string;
  email?: string;
  prefix?: string;
  clientId?: string;
  encryptKey?: string;
  secretKey?: string;
}

export interface ConnectBankResult {
  configBankId: string;
  isConnected: boolean;
  isOTPConfirmation: boolean;
}

export interface ConfirmOtpRequest {
  configBankId: string;
  otpNumber: string;
}

export interface ConfirmOtpResult {
  configBankId: string;
  vaNumber: string;
  isConnected: boolean;
}

export interface ConnectUrlResult {
  connectUrl: string;
}

export interface DisconnectBankRequest {
  configBankId: string;
}

/**
 * What one T-PayGate delivery did, mirroring the backend TPayGateWebhookOutcome enum member for
 * member. Only `Settled` moves money; every other value leaves the payment as it was.
 */
export const TPAYGATE_WEBHOOK_OUTCOMES = [
  "Settled",
  "AlreadySettled",
  "UnknownReference",
  "NotATPayGateBill",
  "PaymentClosed",
  "AmountMismatch",
  "CurrencyNotSupported",
  "OrderNotPayable",
  "RenewalBlocked",
  "FulfilmentNotApplied",
] as const;
export type TPaygateWebhookOutcome = (typeof TPAYGATE_WEBHOOK_OUTCOMES)[number];

/** The six fields of T-PayGate's own payment callback. */
export interface SimulateWebhookRequest {
  /** The payment code (WIC-PAY-…) sent to T-PayGate as the bill's reference. */
  refTransactionId: string;
  billCode: string;
  /** Smallest currency unit — đồng for VND. Settles only when it equals the bill's amount. */
  amount: number;
  virtualAccount?: string;
  actualAccount?: string;
  /** ISO 8601. Omitted means "now". */
  paymentTime?: string;
}

export interface WebhookSimulationResult {
  /** A `TPaygateWebhookOutcome`, or the settlement's error code when it failed
   * (WEBHOOK_CONFLICT, WEBHOOK_FAILED, SETTLEMENT_INCOMPLETE). */
  outcome: TPaygateWebhookOutcome | string;
  settled: boolean;
  message?: string | null;
  refTransactionId: string;
  reportedAmount: number;
  paymentId?: string | null;
  paymentStatus?: string | null;
  expectedAmount?: number | null;
  currency?: string | null;
  orderNumber?: string | null;
  orderType?: string | null;
  orderStatus?: string | null;
}
